import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { subscriptions, isApiError } from '@repletoryreach/sdk-api';
import { useAuth } from '@repletoryreach/sdk-auth';
import { Stack, Card, Group, Text, Button, SimpleGrid, Badge, Title } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { Modal } from '@repletoryreach/sdk-ui/overlays';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { formatMinor, formatDate } from '../../../lib/format';
import { useCredits, formatCreditAmount } from '../../../lib/credits';
import { sampleRates, planRate, isCheaperPlan, type SampleRate } from '../../../lib/rates';
import { useRates, RATES_KEY } from '../../../data/useRates';
import { useT } from '../../../i18n/I18nProvider';
import { InfoIcon } from '../../../components/panel';
import { Tooltip } from '@repletoryreach/sdk-ui/overlays';

type Plan = subscriptions.Plan;
type Subscription = subscriptions.Subscription;

/**
 * The 402 a plan's fee is refused with. `available_minor` is the BOUGHT
 * credits only, because those are all a fee may be paid from; `included_minor`
 * is what the plan itself put in the wallet, which is for messages.
 */
interface PlanRefusal {
  plan: Plan;
  required_minor: number;
  available_minor: number;
  included_minor: number;
  shortfall_minor: number;
  currency: string;
}

/**
 * Only the refusal that needs explaining: a wallet that still holds credits,
 * all of them the plan's own. An empty wallet keeps the message it always had.
 */
function planRefusal(err: unknown, plan: Plan): PlanRefusal | null {
  if (!isApiError(err) || err.status !== 402 || err.code !== 'INSUFFICIENT_CREDITS') return null;
  const e = (err.body as { error?: Partial<Omit<PlanRefusal, 'plan'>> } | null)?.error;
  if (!e || !((e.included_minor ?? 0) > 0)) return null;
  const required = e.required_minor ?? 0;
  const available = e.available_minor ?? 0;
  return {
    plan,
    required_minor: required,
    available_minor: available,
    included_minor: e.included_minor ?? 0,
    shortfall_minor: e.shortfall_minor ?? Math.max(0, required - available),
    currency: e.currency ?? plan.currency
  };
}

// The price list decides how many plans there are, so the grid follows it: up to
// five across on a wide screen, wrapping to three and two below that, inside a
// dialog wide enough to hold them. Three plans keep the three-column dialog they
// always had rather than three narrow cards beside two empty columns.
const planCols = (count: number) => ({ base: 1, sm: Math.min(2, count), lg: Math.min(3, count), xl: Math.min(5, count) });
const WIDE_DIALOG = 'min(1240px, 96vw)'; // i18n-ignore: a CSS width, not words

/** What a plan allows, in the order it is shown. The keys of `plan.limits`. */
const LIMIT_KEYS = [
  'bots', 'numbers', 'team_members', 'flows', 'knowledge_documents', 'own_whatsapp_account',
  // Bookings and Tickets (7 October 2026).
  'booking_staff', 'booking_services', 'calendar_sync', 'ticket_types', 'ticket_automations',
  // Extra use (30 October 2026): keep going after the credit runs out, on Growth and Scale.
  'extra_use'
] as const;

/** Allowances that are a yes or no rather than a count: 0 is "not included", no ceiling is "included". */
const SWITCH_KEYS: ReadonlySet<string> = new Set(['own_whatsapp_account', 'calendar_sync', 'extra_use']);

/** Rows with a short hint behind an info icon: entry points do not count towards a plan's flows (30 October 2026). */
const LIMIT_HINTS: Record<string, string> = { flows: 'billing.plan.limit.flows.hint', extra_use: 'billing.plan.limit.extra_use.hint' };

/** Where the marketing site's "Pick Growth" lands: the plan it named, remembered across sign-up. */
export const SIGNUP_PLAN_KEY = 'rr.signupPlan';

/**
 * The plan the organisation is on, and the way to compare and change it. The
 * feature lines come from the platform, the same ones the public price list
 * shows, so a customer sees in the console exactly what they were promised.
 */
/**
 * `byInvoice` (18 October 2026): the business pays by invoice, so its plan is paid from the credit its payments are
 * added as, and RepletoryReach changes it. The plan shows; the controls to change it do not.
 *
 * `extraActive` (30 October 2026): extra use is on and working, so the plan reads "Growth+" or "Scale+".
 */
export function PlanCard({ subscription, loading, byInvoice = false, extraActive = false }: {
  subscription: Subscription | null;
  loading: boolean;
  byInvoice?: boolean;
  extraActive?: boolean;
}) {
  const t = useT();
  const credits = useCredits();
  const [params, setParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState<string | null>(null);

  const plansQ = useQuery({ queryKey: ['plans'], queryFn: () => subscriptions.listPlans(), retry: false });
  const plans = useMemo(() => plansQ.data?.data ?? [], [plansQ.data]);
  const current = useMemo(() => plans.find((p) => p.id === subscription?.plan_id) ?? null, [plans, subscription]);
  const currentName = current ? (extraActive ? `${current.name}+` : current.name) : '';
  const qc = useQueryClient();
  // A cancelled plan runs to the end of the month already paid for; until then it can be kept.
  const endsAt = subscription && subscription.status !== 'cancelled' ? subscription.ends_at ?? null : null;
  // A cheaper plan chosen this month takes over at the next renewal (11 October 2026).
  const pendingPlan = useMemo(() => plans.find((p) => p.id === subscription?.pending_plan_id) ?? null, [plans, subscription]);
  const keep = useMutation({
    mutationFn: () => subscriptions.resume(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['subscription'] });
      notifySuccess(t('billing.plan.kept', { name: current?.name ?? '' }), t('billing.plan.title'));
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('billing.plan.failed'))
  });

  // A plan chosen on the public site (?plan=growth on the way in, or kept
  // through sign-up) opens the chooser on that plan once, then is forgotten.
  useEffect(() => {
    if (plans.length === 0 || byInvoice) return;
    let wanted = params.get('plan');
    if (!wanted) {
      try { wanted = sessionStorage.getItem(SIGNUP_PLAN_KEY); } catch { wanted = null; }
    }
    if (!wanted) return;
    try { sessionStorage.removeItem(SIGNUP_PLAN_KEY); } catch { /* storage may be unavailable */ }
    if (params.get('plan')) {
      setParams((prev) => { const q = new URLSearchParams(prev); q.delete('plan'); return q; }, { replace: true });
    }
    const plan = plans.find((p) => p.code === wanted);
    if (plan && plan.id !== current?.id) { setHighlight(plan.code); setOpen(true); }
  }, [plans, current, params, setParams, byInvoice]);

  return (
    <Card withBorder>
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start" wrap="wrap">
          <Stack gap="xs" style={{ minWidth: 0 }}>
            <Group gap="sm" align="center">
              <Title order={4}>{t('billing.plan.title')}</Title>
              {current && <Badge size="sm" variant="light" color="blue" data-testid="plan-name">{currentName}</Badge>}
            </Group>
            <Text role="bodySmall" c="var(--rr-color-text-muted)">
              {loading || plansQ.isPending
                ? t('common.loading')
                : plansQ.error
                  ? t('billing.plan.loadError')
                  : current
                    ? `${t('billing.plan.current', { name: currentName })} ${current.tagline ?? ''} ${subscription ? (endsAt ? t('billing.plan.ends', { date: formatDate(endsAt) }) : pendingPlan ? t('billing.plan.changesTo', { name: pendingPlan.name, date: formatDate(subscription.billing_cycle_end) }) : t('billing.plan.renews', { date: formatDate(subscription.billing_cycle_end) })) : ''}`.trim()
                    : t('billing.plan.none')}
            </Text>
          </Stack>
          {byInvoice ? (
            <Badge size="sm" variant="light" color="teal">{t('billing.plan.byInvoice.badge')}</Badge>
          ) : (
            <Group gap="xs">
              {endsAt && (
                <Button size="sm" onClick={() => keep.mutate()} loading={keep.isPending}>{t('billing.plan.keep')}</Button>
              )}
              <Button size="sm" variant={current ? 'outline' : 'filled'} onClick={() => { setHighlight(null); setOpen(true); }} disabled={plans.length === 0}>
                {current ? t('billing.plan.changePlan') : t('billing.plan.seePlans')}
              </Button>
            </Group>
          )}
        </Group>
        {byInvoice && (
          <Text role="bodySmall" c="var(--rr-color-text-muted)" data-testid="plan-by-invoice">{t('billing.plan.byInvoice.body')}</Text>
        )}
        {current && current.features.length > 0 && (
          <ul className="rr-plan-features rr-plan-features--compact">
            {current.features.slice(0, 4).map((f) => <li key={f}>{f}</li>)}
          </ul>
        )}
      </Stack>
      {open && !byInvoice && (
        <PlanChooserModal
          opened={open}
          onClose={() => setOpen(false)}
          plans={plans}
          current={current}
          subscription={subscription}
          highlight={highlight}
        />
      )}
    </Card>
  );
}

function PlanChooserModal({ opened, onClose, plans, current, subscription, highlight }: {
  opened: boolean;
  onClose: () => void;
  plans: Plan[];
  current: Plan | null;
  subscription: Subscription | null;
  highlight: string | null;
}) {
  const t = useT();
  const credits = useCredits();
  const qc = useQueryClient();
  const { user } = useAuth();

  // One request carries the price on every plan, so each card can say what a
  // message costs on it. Quoted for the country the organisation mostly writes
  // to, else South Africa.
  const ratesQ = useRates();
  const samples = useMemo(
    () => sampleRates(ratesQ.data?.data ?? [], user?.org?.default_country ?? user?.org?.country_iso),
    [ratesQ.data, user]
  );

  // A refusal that needs more than a toast: it has figures and a way out, and
  // the chooser may be scrolled past the top of a phone screen when it lands.
  const [refusal, setRefusal] = useState<PlanRefusal | null>(null);
  const refusalRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (refusal) refusalRef.current?.scrollIntoView({ block: 'nearest' });
  }, [refusal]);

  // A saved card that was declined (11 October 2026): the reason, and a way to use another card.
  const [declined, setDeclined] = useState<string | null>(null);
  const returnUrl = `${window.location.origin}/billing?topup=returned`;
  const newCard = useMutation({
    mutationFn: () => subscriptions.addCard(returnUrl),
    onSuccess: (r) => window.location.assign(r.data.checkout_url),
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('billing.card.title'))
  });

  const change = useMutation({
    mutationFn: async (plan: Plan) => (subscription && subscription.status !== 'cancelled'
      ? (await subscriptions.update({ plan_code: plan.code, return_url: returnUrl })).data
      : (await subscriptions.create({ plan_code: plan.code, return_url: returnUrl })).data),
    onMutate: () => { setRefusal(null); setDeclined(null); },
    onSuccess: (res, plan) => {
      // No card saved yet: PayFast takes the payment and saves the card; the plan starts when it confirms.
      if (subscriptions.isCardCheckout(res)) { window.location.assign(res.checkout_url); return; }
      qc.invalidateQueries({ queryKey: ['subscription'] });
      // A cheaper plan takes over at the next renewal; this month is already paid for.
      if (res && 'pending_plan_id' in res && res.pending_plan_id) {
        notifySuccess(t('billing.plan.changeScheduled', { name: plan.name, date: formatDate(res.billing_cycle_end) }), t('billing.plan.title'));
        onClose();
        return;
      }
      // Moving down to the free plan is cancelling: the paid plan stays until the end of its month.
      if (res && 'ends_at' in res && res.ends_at) {
        notifySuccess(t('billing.plan.cancelScheduled', { date: formatDate(res.ends_at) }), t('billing.plan.title'));
        onClose();
        return;
      }
      qc.invalidateQueries({ queryKey: ['wallet'] });
      qc.invalidateQueries({ queryKey: ['ledger'] });
      // The plan decides the price of every message from here on.
      qc.invalidateQueries({ queryKey: RATES_KEY });
      notifySuccess(t('billing.plan.switched', { name: plan.name }), t('billing.plan.title'));
      onClose();
    },
    onError: (e, plan) => {
      // Credits in the wallet, none of them able to pay a fee: say which part
      // is which and how much is missing, instead of "not enough credit" over
      // a balance that plainly is not empty.
      const refused = planRefusal(e, plan);
      if (refused) { setRefusal(refused); return; }
      if (isApiError(e) && e.code === 'CARD_DECLINED') { setDeclined(e.message); return; }
      // Moving up charges what extra use is owed with the plan (30 October 2026); a charge still being confirmed waits.
      if (isApiError(e) && e.code === 'EXTRA_USE_PENDING') { notifyError(t('billing.plan.extraUsePending'), t('billing.plan.failed')); return; }
      notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('billing.plan.failed'));
    }
  });

  /**
   * What taking a paid plan now costs. Plans are charged on the 1st (4 October 2026), so the first charge is
   * the rest of this month with the same share of the plan's credits; the service works it out. Without an
   * answer the dialog says the whole-month wording rather than a figure it cannot vouch for.
   */
  async function subscribeText(plan: Plan, price: string, credit: string): Promise<string> {
    try {
      const q = (await subscriptions.quote(plan.code)).data;
      if (q.full_month) return t('billing.plan.confirm.subscribeFull', { amount: price, credit });
      return t('billing.plan.confirm.subscribe', {
        now: formatMinor(q.charge_now_minor, q.currency),
        nowCredit: credits(q.credits_now_minor, q.currency),
        next: formatDate(q.next_charge_at),
        amount: price,
        credit
      });
    } catch {
      return t('billing.plan.confirm.subscribeFull', { amount: price, credit });
    }
  }

  async function choose(plan: Plan) {
    const price = formatMinor(plan.monthly_amount_minor, plan.currency);
    const credit = credits(plan.included_credits_minor, plan.currency);
    const onPaidPlan = Boolean(subscription && subscription.status !== 'cancelled' && current && current.monthly_amount_minor > 0);
    const body = onPaidPlan && plan.monthly_amount_minor === 0 && subscription
      ? t('billing.plan.confirm.cancelAtEnd', { name: current?.name ?? '', date: formatDate(subscription.ends_at ?? subscription.billing_cycle_end), free: plan.name })
      : subscription && subscription.status !== 'cancelled'
      ? (plan.monthly_amount_minor < (current?.monthly_amount_minor ?? 0)
        ? t('billing.plan.confirm.downgrade', { name: plan.name, date: formatDate(subscription.billing_cycle_end) })
        : t('billing.plan.confirm.change'))
      : plan.monthly_amount_minor > 0
        ? await subscribeText(plan, price, credit)
        : t('billing.plan.confirm.subscribeFree');
    openConfirmModal({
      title: t('billing.plan.confirm.title', { name: plan.name }),
      children: <Text role="bodySmall">{body}</Text>,
      labels: { confirm: t('billing.plan.confirm.action'), cancel: t('action.cancel') },
      onConfirm: () => change.mutate(plan)
    });
  }

  return (
    <Modal opened={opened} onClose={onClose} title={t('billing.plan.modalTitle')} size={plans.length > 3 ? WIDE_DIALOG : 'xl'}>
      <Stack gap="md">
        <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('billing.plan.modalBody')}</Text>
        {refusal && (
          <div ref={refusalRef}>
            <Alert color="error" role="alert" title={t('billing.plan.refused.title', { name: refusal.plan.name })}>
              <Stack gap={8}>
                <Text role="bodySmall">
                  {t('billing.plan.refused.body', {
                    bought: credits(refusal.available_minor, refusal.currency),
                    required: credits(refusal.required_minor, refusal.currency),
                    included: credits(refusal.included_minor, refusal.currency)
                  })}
                </Text>
                <Text role="bodySmall">
                  {t('billing.plan.refused.next', { shortfall: credits(refusal.shortfall_minor, refusal.currency), name: refusal.plan.name })}{' '}
                  <Link to="/billing/topup" className="rr-billing__link">{t('billing.topUpNow')}</Link>
                </Text>
              </Stack>
            </Alert>
          </div>
        )}
        {declined && (
          <Alert color="error" role="alert" title={t('billing.card.declinedTitle')}>
            <Stack gap={8}>
              <Text role="bodySmall">{declined}</Text>
              <div><Button size="xs" onClick={() => newCard.mutate()} loading={newCard.isPending}>{t('billing.card.useAnother')}</Button></div>
            </Stack>
          </Alert>
        )}
        <SimpleGrid cols={planCols(Math.max(plans.length, 1))} spacing="md">
          {plans.map((plan) => {
            const isCurrent = plan.id === current?.id;
            const featured = plan.code === highlight || (!highlight && Boolean(plan.badge));
            return (
              <div key={plan.id} className={`rr-plan${featured ? ' rr-plan--featured' : ''}${isCurrent ? ' rr-plan--current' : ''}`}>
                <Stack gap="sm">
                  <Group gap="sm" align="center" justify="space-between">
                    <Text role="subtitle" fw={700}>{plan.name}</Text>
                    {plan.badge && <Badge size="xs" variant="light" color="blue">{plan.badge}</Badge>}
                  </Group>
                  {plan.tagline && <Text role="bodySmall" c="var(--rr-color-text-muted)">{plan.tagline}</Text>}
                  <Text role="title" fw={700}>
                    {plan.monthly_amount_minor > 0 ? t('billing.plan.perMonth', { amount: formatMinor(plan.monthly_amount_minor, plan.currency) }) : t('billing.plan.free')}
                  </Text>
                  {plan.included_credits_minor > 0 && (
                    <Text role="bodySmall" fw={600} c="var(--rr-color-primary)">
                      {t('billing.plan.includedCredit', { amount: credits(plan.included_credits_minor, plan.currency) })}
                    </Text>
                  )}
                  <PlanLimits plan={plan} />
                  <PlanPrices plan={plan} samples={samples} cheaper={!isCurrent && isCheaperPlan(samples, plan.code)} />
                  <ul className="rr-plan-features">
                    {plan.features.map((f) => <li key={f}>{f}</li>)}
                  </ul>
                  <Button
                    size="sm"
                    variant={isCurrent ? 'outline' : featured ? 'filled' : 'light'}
                    disabled={isCurrent || change.isPending}
                    loading={change.isPending && change.variables?.id === plan.id}
                    onClick={() => void choose(plan)}
                  >
                    {isCurrent ? t('billing.plan.currentPlan') : t('billing.plan.choose', { name: plan.name })}
                  </Button>
                </Stack>
              </div>
            );
          })}
        </SimpleGrid>
      </Stack>
    </Modal>
  );
}

/**
 * What the plan allows, whole, on every card. The feature lines under it say
 * only what a plan adds to the one before, so on their own they never say that
 * Lite still has one chatbot and one number; this does.
 */
function PlanLimits({ plan }: { plan: Plan }) {
  const t = useT();
  const limits = (plan.limits ?? {}) as Record<string, number | null | undefined>;
  const rows = LIMIT_KEYS.filter((k) => k in limits);
  if (rows.length === 0) return null;
  return (
    <dl className="rr-plan-limits" aria-label={t('billing.plan.limits.title')}>
      {rows.map((k) => (
        <div key={k} className="rr-plan-limits__row">
          <dt>
            {t(`billing.plan.limit.${k}`)}
            {LIMIT_HINTS[k] && (
              // A button, not a bare icon: the tooltip opens on focus as well as hover.
              <Tooltip label={t(LIMIT_HINTS[k])} position="top" maxWidth={260}>
                <button type="button" className="rr-billing__info" aria-label={t(LIMIT_HINTS[k])}>
                  <InfoIcon />
                </button>
              </Tooltip>
            )}
          </dt>
          <dd>
            {SWITCH_KEYS.has(k)
              ? t(limits[k] === 0 ? 'billing.plan.limit.notIncluded' : 'billing.plan.limit.included')
              : limits[k] == null ? t('billing.plan.limit.unlimited') : String(limits[k])}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * What a message costs on the plan: the higher the plan, the less each one
 * costs, and that is half of what a customer is choosing between. Three lines
 * from the price list, bare figures under a heading that says credits so five
 * cards still fit side by side. Nothing renders until the price list has
 * figures for this plan.
 */
function PlanPrices({ plan, samples, cheaper }: { plan: Plan; samples: SampleRate[]; cheaper: boolean }) {
  const t = useT();
  const rows = samples.flatMap((s) => {
    const minor = planRate(s.rate, plan.code);
    return minor === undefined ? [] : [{ kind: s.kind, minor }];
  });
  if (rows.length === 0) return null;
  return (
    <div className="rr-plan-prices">
      <Text role="caption" fw={600} c="var(--rr-color-text-muted)">{t('billing.plan.prices.title')}</Text>
      <dl className="rr-plan-prices__list" aria-label={t('billing.plan.prices.title')}>
        {rows.map((r) => (
          <div key={r.kind} className="rr-plan-limits__row">
            <dt>{t(`billing.plan.prices.${r.kind}`)}</dt>
            <dd>{formatCreditAmount(r.minor)}</dd>
          </div>
        ))}
      </dl>
      {cheaper && <Text role="caption" fw={600} c="var(--rr-color-primary)">{t('billing.plan.prices.cheaper')}</Text>}
    </div>
  );
}
